import GroupIcon from "@mui/icons-material/GroupOutlined";
import type { StackProps } from "@mui/material";
import {
  Box,
  Checkbox,
  FormHelperText,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import NumberAvatar from "../common/NumberAvatar";
import { getApprovalMethodDescription } from "./approvalsHelper";
import type { ApproverAssignmentValidationError, ApproversAssignmentStep } from "./workflows/approversAssignmentState";

interface Props {
  steps: ApproversAssignmentStep[];
  validationErrors: ApproverAssignmentValidationError[];
  onSelectionChange: (stepName: string, ruleIndex: number, userId: string) => void;
  containerProps?: StackProps;
}

const ApprovalAssignmentEditor = ({ steps, validationErrors, onSelectionChange, containerProps }: Props) => {
  return (
    <Stack spacing={2} {...containerProps}>
      {steps.map((step, stepIndex) => (
        <Box key={step.name}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <NumberAvatar number={stepIndex + 1} size={24} />
            <Typography variant="subtitle1">{step.name}</Typography>
          </Stack>

          {step.rules.map((rule) => (
            <Box key={rule.ruleIndex} sx={{ pl: 4 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", py: 1 }}>
                <GroupIcon color="action" />
                <Typography variant="subtitle2">{getApprovalMethodDescription(rule.approvalMethod)}</Typography>
              </Stack>
              {validationErrors.find((error) => error.stepName === step.name && error.ruleIndex === rule.ruleIndex) && (
                <FormHelperText error>Select at least one approver</FormHelperText>
              )}
              <List sx={{ pl: 3, pr: 0, py: 0 }} dense>
                {rule.users.map((user) => (
                  <ListItem disablePadding key={user.id}>
                    <ListItemButton
                      role={undefined}
                      dense
                      onClick={() => onSelectionChange(step.name, rule.ruleIndex, user.id)}
                    >
                      <Checkbox checked={user.selected} edge="start" tabIndex={-1} disableRipple />
                      <ListItemText
                        primary={
                          <Typography color={user.selected ? "textPrimary" : "textSecondary"}>{user.name}</Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
      ))}
    </Stack>
  );
};

export default ApprovalAssignmentEditor;
